<template>
  <v-card class="VoucherDetails"
    @keydown.enter="save"
  >
    <v-card-title>
      <span v-if="isNewItem" class="subtitle-1">New Item</span>
      <span v-if="!isNewItem" class="subtitle-1">Edit Item</span>
    </v-card-title>
    <v-card-text>
      <v-form ref="form">
        <v-container :class="containerClass">
          <v-row>
            <v-col cols="2">
              <v-text-field class="odtrans fiscalYear input-text-size"
                id="odTransFYText"
                dense
                hide-details="auto"
                v-model.number="internalValue.fiscalYear"
                label="Fiscal Year"
                type="number"
                @input="voucherFYChange"
                :rules="fiscalYearValidation"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select class="odtrans type input-text-size"
                id="odTransTypeSelect"
                dense
                hide-details="auto"
                :readonly="isTransactionTypeReadOnly"
                v-model="internalValue.type"
                :items="filteredTransTypes"
                :rules="reqdSelectValidation"
                @change="determineNextTransactionNumbers()"
                item-text="description"
                item-value="name"
                label="Type"
              ></v-select>
            </v-col>
            <v-col cols="2">
              <v-tooltip bottom
                :open-on-hover="isTooltipVisible">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field class="odtrans registration input-text-size"
                    id="odTransRegistrationText"
                    dense
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                    v-model.number="internalValue.registration"
                    label="Registration #"
                    color="red"
                    type="number"
                    :readonly="!registrationOverride || isNewItem"
                    :class="voucherRegPulseClass"
                    @dblclick.stop="overrideRegistrationNumber"
                  ></v-text-field>
                </template>
                <span>{{registrationHoverCaption}}</span>
              </v-tooltip>
            </v-col>
            <v-col cols="4">
              <ClarionDateControl class="odtrans register input-text-size"
                id="odTransRegisterDate"
                dense
                hide-details="auto"
                v-model="internalValue.register"
                :isRequired="true"
                :isIsoDate="true"
                :isToggle="false"
                :label="`Register Date`"
                :rules="[registerDateValidation, ...registerDateRules]"
                @blurred="registerDateBlurred"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
        <v-autocomplete
          class="odtrans department input-text-size"
          id="odTransDepartmentSelect"
          dense
          hide-details="auto"
          :autofocus="autoFocus.isDepartment"
          v-model="internalValue.department"
          :items="departments"
          item-text="caption"
          item-value="id"
          :label="departmentLabel"
          :rules="reqdSelectValidation"
          @change="updateDeptBalance"
        ></v-autocomplete>
      </v-col>
            <v-col cols="3">
              <VoucherNumberInput class="odtrans voucher"
                id="odTransVoucherInput"
                dense
                hide-details="auto"
                v-model.number="internalValue.voucher"
                :department="internalValue.department"
                :fiscalYear="internalValue.fiscalYear"
                :isNewItem="isNewItem"
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete class="odtrans whom input-text-size"
                id="odTransToWhom"
                dense
                hide-details="auto"
                :autofocus="autoFocus.isToWhom"
                v-model="internalValue.whomStr"
                :items="whoms"
                :rules="reqdSelectValidation"
                label="To Whom"
                @keydown="whomShortcutHandler"
                @update:list-index="whomUpdateSearchInputByNumber"
                @update:search-input="whomUpdateSearchInput"
                @focus="onFocusAutocomplete"
              >
                </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-autocomplete class="odtrans what input-text-size"
                id="odTransForWhat"
                dense
                hide-details="auto"
                ref="autocompleteForWhat"
                v-model="internalValue.whatStr"
                :items="whats"
                label="For What"
                @keydown="whatShortcutHandler"
                @update:list-index="whatUpdateSearchInputByNumber"
                @update:search-input="whatUpdateSearchInput"
              >
                </v-autocomplete>
            </v-col>
            <v-col cols="3" sm="4" md="2">
              <v-text-field class="odtrans amount input-text-size"
                id="odTransAmountText"
                dense
                hide-details="auto"
                :autofocus="autoFocus.isAmount"
                v-model="internalValue.amount"
                ref="amount"
                :rules="amountValidation"
                label="Amount"
                @blur="roundAmount"
                @focus="focusAmount($event); amountToNumber()"
              ></v-text-field>
            </v-col>
          <v-col cols="2" >
        <v-checkbox v-model="internalValue.isEftCode"
          class="input-text-size"
          dense
          hide-details
          label="EFT"
          @change="onEftCodeChange"
        ></v-checkbox>
        </v-col>
            <v-col cols="2">
              <v-checkbox class="odtrans isCorrected input-text-size"
                id="odTransIsCorrected"
                dense
                hide-details
                v-model="internalValue.isCorrected"
                label="Corrected"
                @change="onCorrectedChange"
              ></v-checkbox>
            </v-col>
            <v-col cols="3" sm="3" md="2">
              <v-text-field class="odtrans correctedAmount input-text-size"
                id="odTransCorrectedAmountText"
                dense
                hide-details="auto"
                v-model="internalValue.correctedAmount"
                :disabled="!internalValue.isCorrected"
                :rules="correctedAmountValidation"
                label="Corrected Amount"
                @blur="roundCorrectedAmount"
                @focus="focusAmount($event); correctedAmountToNumber()"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3" sm="5" md="3">
              <v-select class="odtrans status input-text-size"
                id="odTransStatusSelect"
                dense
                hide-details="auto"
                v-model="internalValue.status"
                :items="voucherStatusOptions"
                :rules="reqdSelectValidation"
                :hint="voidAgeLockedHint"
                persistent-hint
                item-text="description"
                item-value="name"
                label="Status"
              ></v-select>
            </v-col>
            <v-col cols="3" sm="5" md="3">
              <ClarionDateControl class="odtrans finalized input-text-size"
                id="odTransFinalPaidDate"
                dense
                hide-details="auto"
                v-model="internalValue.finalized"
                :isRequired="false"
                :isIsoDate="true"
                :isToggle="false"
                :label="`Date Pd/Can`"
              />
            </v-col>
            <v-col>
              <v-text-field class="odtrans remarks input-text-size"
                id="odTransRemarksText"
                dense
                hide-details="auto"
                v-uppercase=globalSettingItem.conEverythingToCaps
                v-model="internalValue.remarks"
                label="Remarks"
                type="string"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <Address class="odtrans voucherAddress"
              id= "odTransPayVoucherAddress"
              :address="internalValue.voucherAddress || {}"
              @update:address="updateVoucherAddress"
              caption="Pay to the order of:" />
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn class="odtrans print"
        id="odTransPrint"
        text color="blue darken-1"
        @click="print"
      >
        <v-icon>{{icons.mdiPrinter}}</v-icon>
        Print
      </v-btn>
      <v-spacer></v-spacer>
      <AuditDialog id="odTransAuditDialog"
        class="od-trans-audit-dialog"
        :identifier="internalValue.id"
        activatorCaption="Audit History"
      />
      <UploadFile
        class="odtrans uploadFile"
        bucketName="OD"
        program="OD"
        parentObjectType="ODTransaction"
        :parentObjectId="internalValue.id"
        :itemIsSaved="!isNewItem">
        <template v-slot:activator="{ activatorProps }">
          <v-btn
            color="primary"
            text
            @contextmenu.prevent="imagesRightClick(activatorProps.rightClick)"
            @click="imagesLeftClick(activatorProps.leftClick)"
          >
            {{activatorProps.caption}}
          </v-btn>
        </template>
      </UploadFile>
      <v-btn class="odtrans close"
        id="odTransClose"
        text color="blue darken-1"
        @click="close"
      >
        Cancel
      </v-btn>
      <v-btn class="odtrans save"
        id="odTransSave"
        text color="blue darken-1"
        @click="save"
      >
        Save
      </v-btn>
      <div v-if="isNewItem">(Shift+Enter to Duplicate)</div>
    </v-card-actions>
  </v-card>
</template>
<script>
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import DepositVoucherMixin from './DepositVoucher.mixin';
import VoucherNumberInput from './VoucherNumberInput.vue';
import ClarionDateControl from '../common/ClarionDateControl.vue';
import Address from '../common/Address.vue';
import UploadFile from '../common/UploadFile.vue';
import AuditDialog from '../common/audit/AuditDialog.vue';
import { clients } from '../../util/clients';
import { printerHandler } from '../../util/pdf-to-printer.util';
import {
  isNotBeforeDate,
  toIsoDate,
  isVoucherAgeLocked,
} from '../../util/shared/tmc-global';
import {
  onFocusAutocomplete,
  focusNextField,
} from '../../util/shared/vue-global';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

const { backendRest } = clients.direct;

export default {
  name: 'VoucherDetails',
  mixins: [DepositVoucherMixin, GlobalToCaps],
  data: () => ({
    whatLastSearched: undefined,
    whomLastSearched: undefined,
  }),
  components: {
    VoucherNumberInput,
    ClarionDateControl,
    UploadFile,
    AuditDialog,
    Address,
  },
  props: {
    editedItem: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    internalValue: {
      get() {
        return this.editedItem;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    whats() {
      const whatList = (this.$store.state.OD.whats || []).map((w) => w.description || '');
      whatList.push(this.editedItem.whatStr || '');
      return whatList;
    },
    whoms() {
      const whomList = (this.$store.state.OD.whoms || []).map((w) => w.description || '');
      whomList.push(this.editedItem.whomStr || '');
      return whomList;
    },
    ...mapState({
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
    }),
    ...mapGetters('user', [
      'getSettings',
    ]),
    voucherAgeLocked() {
      return this.internalValue.status !== 'VOID' && isVoucherAgeLocked(this.internalValue);
    },
    voucherStatusOptions() {
      if (!this.voucherAgeLocked) return this.statuses;
      return this.statuses.filter((s) => s.name !== 'VOID');
    },
    voidAgeLockedHint() {
      return this.voucherAgeLocked
        ? 'Registered more than two business days ago -- can no longer be voided.'
        : '';
    },
  },
  methods: {
    onFocusAutocomplete,
    focusNextField,
    ...mapActions('OD', [
      'upsWhat',
      'loadWhats',
      'upsWhom',
      'loadWhoms',
      'upsTransaction',
    ]),
    ...mapActions([
      'flashError',
      'flashInfo',
      'reAuth',
    ]),
    voucherFYChange() {
      // not super stable yet (TODO - research why)
      // this.whatShortcutHandler({ key: 'Tab' });
      // this.whomShortcutHandler({ key: 'Tab' });
      this.updateDeptBalance();
    },
    onEftCodeChange() {
      if (this.internalValue.isEftCode) {
        this.internalValue.voucher = null;
        if (this.voucherRegPolling !== undefined) {
          clearInterval(this.voucherRegPolling);
          this.setVoucherRegPolling(undefined);
        }
      }
    },
    updateVoucherAddress(value) {
      const newInternalValue = {
        ...this.internalValue,
        voucherAddress: value,
      };
      this.setNewEditItem(newInternalValue);
    },
    async print() {
      const { jwt } = await this.reAuth();
      const { register } = this.internalValue;
      const saveTrans = isNotBeforeDate(
        register,
        this.todaysDate,
        (this.durationOfCorrection + 1),
      );
      let isReceiptPrintable;
      const printCounter = (this.internalValue.hasPrinted || 0) + 1;
      if (saveTrans || this.isNewItem) {
        this.internalValue.hasPrinted = printCounter;
        // save print's count as well as edited data
        isReceiptPrintable = await this.internalSave(undefined, false);
      } else {
        const upsert = {
          transaction: {
            id: this.internalValue.id,
            hasPrinted: printCounter,
          },
        };
        // updating print count
        await this.upsTransaction(upsert);
        isReceiptPrintable = true;
      }
      if (isReceiptPrintable) {
        const { id } = this.internalValue;
        const url = `${backendRest.defaults.baseURL}/vouchercheck?odTransId=${id}&token=${jwt}`;
        // Single-check print. This is the same document the bulk action on the
        // transaction list produces, so it routes through the same reportName and
        // lands in whichever tray the county assigned to Checks; with printer
        // mode off it still opens the preview tab exactly as before.
        printerHandler(this, { url, reportName: 'checks', deafultPrinterMode: true });
      }
    },
    whatUpdateSearchInput(val) {
      this.whatLastSearched = val;
    },
    whatUpdateSearchInputByNumber(number) {
      this.whatLastSearched = this.whats[number];
    },
    async whatShortcutHandler(e) {
      if (e.key === 'Tab') {
        const { whatStr, fiscalYear } = this.internalValue;
        const { whatLastSearched } = this;
        const matchWhats = this.whats.filter((w) => w === whatStr);
        const matchWhatLastSearched = this.whats.filter((w) => w === whatLastSearched);
        if (matchWhats.length > 0 || whatLastSearched) {
          const matchWhat = matchWhats[0] ? matchWhats[0] : '';
          if (
            (matchWhat || '').toLowerCase() !== (whatLastSearched || '').toLowerCase()
            && (matchWhatLastSearched.length === 0)
          ) {
            this.flashInfo('Creating a new What entry.');
            // create a new record to match the description typed
            await this.upsWhat({
              _id: '',
              description: whatLastSearched,
              fiscalYear,
            });
            await this.loadWhats({
              fiscalYear,
            });
          }
          this.internalValue.whatStr = whatLastSearched;
        }
        focusNextField(this, e, 'amount');
      }
    },
    whomUpdateSearchInput(val) {
      this.whomLastSearched = val;
    },
    whomUpdateSearchInputByNumber(number) {
      this.whomLastSearched = this.whoms[number];
    },
    async whomShortcutHandler(e) {
      if (e.key === 'Tab') {
        const { whomStr, fiscalYear } = this.internalValue;
        const { whomLastSearched } = this;
        const matchWhoms = this.whoms.filter((w) => w === whomStr);
        const matchWhomLastSearched = this.whoms.filter((w) => w === whomLastSearched);
        if (matchWhoms.length > 0 || whomLastSearched) {
          const matchWhom = matchWhoms[0] ? matchWhoms[0] : '';
          if (
            (matchWhom || '').toLowerCase() !== (whomLastSearched || '').toLowerCase()
            && (matchWhomLastSearched.length === 0)
          ) {
            this.flashInfo('Creating a new Whom entry.');
            // create a new record to match the description typed
            await this.upsWhom({
              _id: '',
              description: whomLastSearched,
              fiscalYear,
            });
            await this.loadWhoms({
              fiscalYear,
            });
          }
          this.internalValue.whomStr = whomLastSearched;
        }
        focusNextField(this, e, 'autocompleteForWhat');
      }
    },
    registerDateValidation(value) {
      const fy = this.internalValue.fiscalYear;
      const fyStart = new Date(`${fy - 1}-07-01`);
      const fyEnd = new Date(`${fy}-07-07`);
      const registerDate = new Date(toIsoDate(value));
      return (
        (registerDate >= fyStart && registerDate <= fyEnd)
        || 'Register Date must fall within the fiscal year'
      );
    },
  },
};
</script>

<style lang="sass" scoped>
  .VoucherDetails
    .v-col
      padding-top: 4px
      padding-bottom: 4px
</style>
